/**
 * UI click — a low, soft click for menu buttons. A tiny pooled HTMLAudio player over one
 * vendored click.ogg, world-FREE (the menu has no World/Sound, which is theme-bound). It
 * is autoplay-safe: the click is always triggered from inside a real pointer/click on a
 * button — itself a user gesture — so it plays from the very first press.
 *
 * `installUiClick()` adds ONE delegated listener so every sticker button (`.sr-btn`, used
 * across the main menu, pause menu, pickers, result card + editor chrome) clicks with no
 * per-button wiring. Idempotent — safe to call once per page (game + editor entries).
 *
 * POLISH_07 #5: UiSound has its own pool (it runs on the world-free menu, which has no
 * engine.Sound instance), so the master SFX gain (maker.sfxVol.v1, same key Sound.ts
 * persists to) is read independently here and multiplied into the per-click volume — a
 * player who turns SFX down in the pause menu also gets quieter menu clicks next boot.
 */
import clickUrl from '../assets/ui/click.ogg?url';
import { parseStoredVolume } from '../engine/Music';

const SFX_VOL_KEY = 'maker.sfxVol.v1';
function readStoredSfxGain(): number {
  try { return parseStoredVolume(localStorage.getItem(SFX_VOL_KEY), 1); } catch { return 1; }
}

const POOL_SIZE = 4;
let pool: HTMLAudioElement[] = [];
let idx = 0;
let muted = false;
let gain = readStoredSfxGain();

function ensurePool(): void {
  if (pool.length) return;
  for (let i = 0; i < POOL_SIZE; i++) {
    const a = new Audio(clickUrl);
    a.preload = 'auto';
    a.volume = 0.3; // low + soft — sits well under music and gameplay SFX
    pool.push(a);
  }
}

/** Play one soft click (round-robins the pool so rapid clicks overlap cleanly). */
export function uiClick(): void {
  if (muted || gain <= 0) return;
  ensurePool();
  const a = pool[idx];
  idx = (idx + 1) % pool.length;
  a.currentTime = 0;
  a.volume = 0.3 * gain;
  void a.play().catch(() => { /* pre-gesture autoplay gate — ignore */ });
}

/** Re-read the persisted master SFX gain (call after Sound.setGain() writes it, so the
 *  same pause-menu slider affects menu clicks too within this page's lifetime). */
export function refreshUiClickGain(): void { gain = readStoredSfxGain(); }

export function setUiClickMuted(m: boolean): void { muted = m; }

let installed = false;
/** Install the global delegated `.sr-btn` click sound. Call once per page. */
export function installUiClick(): void {
  if (installed) return;
  installed = true;
  document.addEventListener('pointerdown', (e) => {
    if ((e.target as Element | null)?.closest('.sr-btn')) uiClick();
  }, { capture: true });
}
